/**
 * Chapter024.js — Hazard Report Form (OHSE 024)
 * Migrated from SWMS Platform Chapter024.tsx → JavaScript
 *
 * Props:
 *   data       {array}   hazard_reports from swmsData
 *   onChange   {fn}      (newReports) => void
 *   companyName {string} from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all resize-y min-h-[52px]';

const RISK_CLASSES = [
  { value: '1', label: 'High (1)' },
  { value: '2', label: 'Medium (2)' },
  { value: '3', label: 'Low (3)' },
];

const WHEN_OPTIONS = [
  { value: 'Immediate', label: 'Immediate' },
  { value: 'Within 24 hrs', label: 'Within 24 hrs' },
  { value: 'Within 7 Days', label: 'Within 7 Days' },
];

function emptyEntry() {
  return {
    id: crypto.randomUUID(),
    date: '', workplace: '', submitted_by: '', submitted_by_signature: '',
    submitted_to: '', submitted_to_signature: '',
    location: '', work_activity: '', hazard_identified: '',
    risk_class: '', corrective_action: '', action_by: '', when_required: '',
    corrective_action_completed_by: '', completed_by_signature: '',
    completion_time: '', completion_date: '', confirmed_by: '', confirmed_by_signature: '',
  };
}

function Tickbox({ checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <input type="checkbox" checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-[#c9a84c] text-[#c9a84c] focus:ring-[#c9a84c] focus:ring-offset-0" />
      <span className="text-sm text-[#1a1a2e]">{label}</span>
    </label>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function Chapter024({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addEntry = useCallback(() => onChange([...data, emptyEntry()]), [data, onChange]);
  const removeEntry = useCallback((id) => onChange(data.filter(e => e.id !== id)), [data, onChange]);
  const updateEntry = useCallback((id, field, value) => {
    onChange(data.map(e => e.id === id ? { ...e, [field]: value } : e));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 024</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Hazard Report Form</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        Where a hazard cannot be immediately corrected,{' '}
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        records the hazard in the Hazard Report.
      </div>

      {/* Report Cards */}
      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No hazard reports yet.</p>
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Hazard Report
          </button>
        </div>
      )}

      {data.map((entry) => (
        <div key={entry.id} className="bg-white border border-[#e0dbd0] rounded-lg p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-[#1a1a2e]">
              {entry.hazard_identified || 'New Hazard Report'}
            </span>
            <button type="button" onClick={() => removeEntry(entry.id)}
              className="p-1 text-red-400 hover:text-red-600 transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* General */}
          <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">General</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Date">
              <input type="date" value={entry.date}
                onChange={e => updateEntry(entry.id, 'date', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Workplace">
              <input type="text" value={entry.workplace} placeholder="Workplace name"
                onChange={e => updateEntry(entry.id, 'workplace', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Submitted By">
              <input type="text" value={entry.submitted_by} placeholder="Name"
                onChange={e => updateEntry(entry.id, 'submitted_by', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Submitted By Signature (type name)">
              <input type="text" value={entry.submitted_by_signature} placeholder="Signature"
                onChange={e => updateEntry(entry.id, 'submitted_by_signature', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Submitted To">
              <input type="text" value={entry.submitted_to} placeholder="Name"
                onChange={e => updateEntry(entry.id, 'submitted_to', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Submitted To Signature (type name)">
              <input type="text" value={entry.submitted_to_signature} placeholder="Signature"
                onChange={e => updateEntry(entry.id, 'submitted_to_signature', e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* Details of Hazard */}
          <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Details of Hazard</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Location">
              <input type="text" value={entry.location} placeholder="Where the hazard is located"
                onChange={e => updateEntry(entry.id, 'location', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Work Activity">
              <input type="text" value={entry.work_activity} placeholder="Activity being performed"
                onChange={e => updateEntry(entry.id, 'work_activity', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Hazard identified in relation to the work activity">
            <textarea value={entry.hazard_identified} placeholder="Describe the hazard identified..."
              onChange={e => updateEntry(entry.id, 'hazard_identified', e.target.value)} className={textareaCls} rows={3} />
          </Field>

          {/* Details of Risk */}
          <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Details of Risk</p>
          <Field label="Risk Class">
            <div className="flex flex-wrap items-center gap-6 mt-1">
              {RISK_CLASSES.map(rc => (
                <Tickbox key={rc.value}
                  label={rc.label}
                  checked={entry.risk_class === rc.value}
                  onChange={checked => updateEntry(entry.id, 'risk_class', checked ? rc.value : '')} />
              ))}
            </div>
          </Field>

          {/* Control Measures */}
          <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Control Measures</p>
          <Field label="Corrective Action Required">
            <textarea value={entry.corrective_action} placeholder="Describe corrective action to be taken..."
              onChange={e => updateEntry(entry.id, 'corrective_action', e.target.value)} className={textareaCls} rows={3} />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="By Whom">
              <input type="text" value={entry.action_by} placeholder="Person responsible"
                onChange={e => updateEntry(entry.id, 'action_by', e.target.value)} className={inputCls} />
            </Field>
            <Field label="When">
              <div className="flex flex-wrap items-center gap-4 mt-1">
                {WHEN_OPTIONS.map(wo => (
                  <Tickbox key={wo.value}
                    label={wo.label}
                    checked={entry.when_required === wo.value}
                    onChange={checked => updateEntry(entry.id, 'when_required', checked ? wo.value : '')} />
                ))}
              </div>
            </Field>
          </div>

          {/* Completion */}
          <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest">Completion</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Corrective Action Completed By">
              <input type="text" value={entry.corrective_action_completed_by} placeholder="Name"
                onChange={e => updateEntry(entry.id, 'corrective_action_completed_by', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Signature (type name)">
              <input type="text" value={entry.completed_by_signature} placeholder="Signature"
                onChange={e => updateEntry(entry.id, 'completed_by_signature', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Time">
              <input type="time" value={entry.completion_time}
                onChange={e => updateEntry(entry.id, 'completion_time', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Date">
              <input type="date" value={entry.completion_date}
                onChange={e => updateEntry(entry.id, 'completion_date', e.target.value)} className={inputCls} />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Confirmed By">
              <input type="text" value={entry.confirmed_by} placeholder="Name"
                onChange={e => updateEntry(entry.id, 'confirmed_by', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Confirmed By Signature (type name)">
              <input type="text" value={entry.confirmed_by_signature} placeholder="Signature"
                onChange={e => updateEntry(entry.id, 'confirmed_by_signature', e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>
      ))}

      {data.length > 0 && (
        <div className="flex justify-center">
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
            <Plus className="w-4 h-4" /> Add Hazard Report
          </button>
        </div>
      )}
    </div>
  );
}
